import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { getAssessments, sendInvitation, resendInvitation } from 'services/vendor-assessment-service';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import { Dialog, Select, TextField } from 'Components/ui';
import { Spinner } from 'Components/common';
import {
  BannerCard, BannerText, BannerTitle, BannerDesc,
  FilterCard, FilterField, FilterLabel, FilterBtn,
  TableCard, StyledTable, TableHead, Th, TableBody, Tr, Td,
  VendorName, FrameworkText, StatusBadge, ScoreBadge, NoScoreText, DateText,
  ActionBtn, ActionsCell, TableFooter, FooterText, EmptyCell, LoadingWrapper,
} from './AssessmentList.styles';

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') return val.en || val.pt || val.es || Object.values(val)[0] || ''
  return String(val)
}

const STATUS_STYLE = {
  'Draft':                     { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
  'Pending Vendor Response':   { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)'   },
  'Under Review':              { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)'  },
  'Pending Executive Approval':{ color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)'   },
  'Approved':                  { color: 'var(--vo-success)', bg: 'var(--vo-success-10)'  },
  'Rejected':                  { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)'   },
  'Contractual Review':        { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' },
};

const SCORE_STYLE = (s) => s >= 90
  ? { color: 'var(--vo-success)', bg: 'var(--vo-success-10)'  }
  : s >= 70
  ? { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)'   }
  : { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)'   };

const STATUS_I18N_KEY = {
  'Draft':                      'draft',
  'Pending Vendor Response':    'pendingVendorResponse',
  'Under Review':               'underReview',
  'Pending Executive Approval': 'pendingExecutiveApproval',
  'Approved':                   'approved',
  'Rejected':                   'rejected',
  'Contractual Review':         'contractualReview',
};

const ClipboardSVG = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <rect x="8" y="2" width="8" height="4" rx="1"/>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="16" x2="15" y2="16"/>
  </svg>
);

const SearchSVG = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const EyeSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const EmailSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

export const AssessmentList = ({ tenant }) => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [vendorFilter, setVendorFilter]       = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [statusFilter, setStatusFilter]       = useState('');
  const [appliedFilters, setAppliedFilters]   = useState({ vendor: '', framework: '', status: '' });

  const { data, isLoading } = useQuery(
    ['assessments', tenant, appliedFilters.status],
    () => getAssessments(tenant, { status: (appliedFilters.status && appliedFilters.status !== 'all') ? appliedFilters.status : undefined }),
    { enabled: !!tenant, keepPreviousData: true }
  );

  const allAssessments = (data?.data?.success && data?.data?.data) || [];

  const assessments = allAssessments.filter(a => {
    const vMatch = !appliedFilters.vendor    || str(a.vendorName).toLowerCase().includes(appliedFilters.vendor.toLowerCase());
    const fMatch = !appliedFilters.framework || str(a.frameworkName).toLowerCase().includes(appliedFilters.framework.toLowerCase());
    return vMatch && fMatch;
  });

  const handleFilter = () => {
    setAppliedFilters({ vendor: vendorFilter, framework: frameworkFilter, status: statusFilter });
  };

  const handleSendInvitation = async (id, vendorName) => {
    try {
      await sendInvitation(id);
      queryClient.invalidateQueries(['assessments']);
      Dialog.fire({
        icon: 'success',
        title: t('gap-analysis:assessment.list.swal.invitationSentTitle'),
        text: t('gap-analysis:assessment.list.swal.invitationSentHtml', { vendorName }),
        timer: 3000,
      });
    } catch (error) {
      Dialog.alert({
        icon: 'error',
        title: t('gap-analysis:assessment.list.swal.errorTitle'),
        message: error.response?.data?.error || t('gap-analysis:assessment.list.swal.errorSend'),
      });
    }
  };

  const handleResendInvitation = async (id, vendorName) => {
    try {
      await resendInvitation(id);
      queryClient.invalidateQueries(['assessments']);
      Dialog.fire({
        icon: 'success',
        title: t('gap-analysis:assessment.list.swal.emailSentTitle'),
        text: t('gap-analysis:assessment.list.swal.emailSentText', { vendorName }),
        timer: 2000,
      });
    } catch (error) {
      Dialog.alert({
        icon: 'error',
        title: t('gap-analysis:assessment.list.swal.errorTitle'),
        message: error.response?.data?.error || t('gap-analysis:assessment.list.swal.errorResend'),
      });
    }
  };

  if (isLoading) {
    return (
      <LoadingWrapper>
        <Spinner $size={24} $borderColor="var(--vo-line)" $speed="0.6s" />
      </LoadingWrapper>
    );
  }

  return (
    <>
      {/* Banner */}
      <BannerCard>
        <ClipboardSVG />
        <BannerText>
          <BannerTitle>{t('gap-analysis:assessment.list.bannerTitle')}</BannerTitle>
          <BannerDesc>{t('gap-analysis:assessment.list.bannerDesc')}</BannerDesc>
        </BannerText>
      </BannerCard>

      {/* Filters */}
      <FilterCard>
        <FilterField $flex>
          <FilterLabel>{t('gap-analysis:assessment.list.filterVendorLabel')}</FilterLabel>
          <TextField
            fullWidth
            value={vendorFilter}
            onChange={e => setVendorFilter(e.target.value)}
            placeholder={t('gap-analysis:assessment.list.filterVendorPlaceholder')}
            onKeyDown={e => e.key === 'Enter' && handleFilter()}
          />
        </FilterField>
        <FilterField $flex>
          <FilterLabel>{t('gap-analysis:assessment.list.filterFrameworkLabel')}</FilterLabel>
          <TextField
            fullWidth
            value={frameworkFilter}
            onChange={e => setFrameworkFilter(e.target.value)}
            placeholder={t('gap-analysis:assessment.list.filterFrameworkPlaceholder')}
            onKeyDown={e => e.key === 'Enter' && handleFilter()}
          />
        </FilterField>
        <FilterField>
          <FilterLabel>{t('gap-analysis:assessment.list.filterStatusLabel')}</FilterLabel>
          <Select
            name="statusFilter"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: '', label: t('gap-analysis:assessment.list.selectStatus') },
              { value: 'all', label: t('gap-analysis:assessment.list.allStatuses') },
              ...Object.entries(STATUS_I18N_KEY).map(([status, key]) => ({
                value: status,
                label: t(`gap-analysis:assessment.list.statuses.${key}`),
              })),
            ]}
          />
        </FilterField>
        <FilterBtn onClick={handleFilter}>
          <SearchSVG />
          {t('gap-analysis:assessment.list.filterButton')}
        </FilterBtn>
      </FilterCard>

      {/* Table */}
      <TableCard>
        <StyledTable>
          <TableHead>
            <tr>
              <Th>{t('gap-analysis:assessment.list.table.vendor')}</Th>
              <Th>{t('gap-analysis:assessment.list.table.framework')}</Th>
              <Th>{t('common:labels.status')}</Th>
              <Th>{t('gap-analysis:assessment.list.table.trustScore')}</Th>
              <Th>{t('gap-analysis:assessment.list.table.createdDate')}</Th>
              <Th $right>{t('common:table.actions')}</Th>
            </tr>
          </TableHead>
          <TableBody>
            {assessments.length === 0 ? (
              <tr>
                <EmptyCell colSpan={6}>{t('gap-analysis:assessment.list.empty')}</EmptyCell>
              </tr>
            ) : (
              assessments.map((assessment) => {
                const statusStyle = STATUS_STYLE[assessment.status] || STATUS_STYLE['Draft'];
                const statusKey   = STATUS_I18N_KEY[assessment.status] || 'draft';
                const scoreStyle  = assessment.trustScore ? SCORE_STYLE(assessment.trustScore) : null;

                return (
                  <Tr key={assessment._id}>
                    <Td><VendorName>{str(assessment.vendorName)}</VendorName></Td>
                    <Td><FrameworkText>{str(assessment.frameworkName)}</FrameworkText></Td>
                    <Td>
                      <StatusBadge $color={statusStyle.color} $bg={statusStyle.bg}>
                        {t(`gap-analysis:assessment.list.statuses.${statusKey}`)}
                      </StatusBadge>
                    </Td>
                    <Td>
                      {scoreStyle ? (
                        <ScoreBadge $color={scoreStyle.color} $bg={scoreStyle.bg}>
                          {assessment.trustScore}%
                        </ScoreBadge>
                      ) : (
                        <NoScoreText>{t('gap-analysis:assessment.list.notScored')}</NoScoreText>
                      )}
                    </Td>
                    <Td><DateText>{formatDateTimeToDateTimeBR(assessment.createdAt)}</DateText></Td>
                    <ActionsCell>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <ActionBtn
                          title={t('gap-analysis:assessment.list.viewDetails')}
                          onClick={() => navigate(`/vendor-assessments/${assessment._id}`)}
                        >
                          <EyeSVG />
                        </ActionBtn>
                        {(assessment.status === 'Draft' || assessment.status === 'Pending Vendor Response') && (
                          <ActionBtn
                            title={assessment.status === 'Draft'
                              ? t('gap-analysis:assessment.list.sendInvitation')
                              : t('gap-analysis:assessment.list.resendInvitation')}
                            onClick={() => assessment.status === 'Draft'
                              ? handleSendInvitation(assessment._id, str(assessment.vendorName))
                              : handleResendInvitation(assessment._id, str(assessment.vendorName))}
                          >
                            <EmailSVG />
                          </ActionBtn>
                        )}
                      </div>
                    </ActionsCell>
                  </Tr>
                );
              })
            )}
          </TableBody>
        </StyledTable>
        <TableFooter>
          <FooterText>
            {t('gap-analysis:assessment.list.showingOf', {
              count: assessments.length,
              total: allAssessments.length,
            })}
          </FooterText>
        </TableFooter>
      </TableCard>
    </>
  );
};
